import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, Host, Output, EventEmitter, ViewChildren } from '@angular/core';
import * as Chart from 'chart.js';
import { ConsumerService } from '../../../services/consumer.service';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { HistoryConsumptionModel } from '../../../models/consumer/history-consumption.model';
import { MeterConsumptionItemComponent } from '../grid-item/meter-consumption-item.component';
import { DataService } from '../../../services/data.service';

@Component({
    selector: 'meter-consumption-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    host: { 'class': 'block-detail' },
    styles: [`:host { width: 70%; position: relative; }`]
})
export class MeterConsumptionChartComponent implements OnInit, OnDestroy {

    @ViewChild('basechart', { static: true }) basechart: any;
    data: HistoryConsumptionModel[] = [];
    chart: any;
    subscriptions: any[] = [];
    timePeriod: number = 1;
    debounceTimeout = 2000;
    debounceId: any;
    consumerId: string;
    allDataSets: any[] = [];
    KwhConsumptionData: any[] = [];
    m3ConsumptionData: any[] = [];

    heatConsumptionChartData: any[] = [];
    heatAConsumptionChartData: any[] = [];
    hotWaterConsumptionChartData: any[] = [];
    coldWaterConsumptionChartData: any[] = [];
    electricityConsumptionData: any[] = [];
    gasConsumptionData: any[] = [];
    chillingConsumtionData: any[] = [];
    VRFConsumptionData: any[] = [];

    heatGradientStroke: any;
    heatAGradientStroke: any;
    hotWaterGradientStroke: any;
    coldWaterGradientStroke: any;
    electricityGradientStroke: any;
    gasGradientStroke: any;
    chillingGradientStroke: any;
    VRFGradientStroke: any;
    canStartAtZero: boolean = false;

    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;

    constructor(private consumerService: ConsumerService,
        private route: ActivatedRoute,
        private dataService: DataService,
        @Host() private parent: MeterConsumptionItemComponent) { }


    getMinHistoryConsumption(input: any[]) {
        return input.reduce((min, p) => p.consumption > min ? p.consumption : min, input[0].consumption);
    }


    ngOnInit() {
        this.clientId = this.route.parent.snapshot.params['clientId'];
        this.allDataSets.push();
        

        this.subscriptions.push(this.route
            .params
            .subscribe(params => {
                this.consumerId = params['consumerId'];
            }));


        this.subscriptions.push(this.route
            .queryParams
            .subscribe(queryParams => {
                if (+queryParams['timePeriod']) {
                    this.timePeriod = +queryParams['timePeriod'];
                }

                this.consumerService.getHistoryConsumption(this.clientId, this.consumerId, this.timePeriod).then((historyConsumptionRes) => {
                    this.data = historyConsumptionRes;
                    this.dataService.meterConsumptionItemComponentResolved.next(historyConsumptionRes.length && historyConsumptionRes.length > 0);
                    this.parent.setExpandVisable(historyConsumptionRes.length && historyConsumptionRes.length > 0);
                    this.KwhConsumptionData = this.data
                        .filter(x => x.unit == "kWh")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.m3ConsumptionData = this.data
                        .filter(x => x.unit == "m3")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.heatConsumptionChartData = this.data
                        .filter(x => x.supplyCategory == "Heat")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.heatAConsumptionChartData = this.data
                        .filter(x => x.supplyCategory == "Heat A")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.hotWaterConsumptionChartData = this.data
                        .filter(x => x.supplyCategory == "Hot Water")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.coldWaterConsumptionChartData = this.data
                        .filter(x => x.supplyCategory == "Cold Water")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.electricityConsumptionData = this.data
                        .filter(x => x.supplyCategory == "Electricity")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.gasConsumptionData = this.data
                        .filter(x => x.supplyCategory == "Gas")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.chillingConsumtionData = this.data
                        .filter(x => x.supplyCategory == "Chilling")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    this.VRFConsumptionData = this.data
                        .filter(x => x.supplyCategory == "VRF")
                        .map(item => {
                            return {
                                x: item.fullDate.toString(),
                                y: item.consumption
                            }
                        });

                    

                    if (this.data.length) {
                        let minHistoryConsumption = this.getMinHistoryConsumption(this.data);
                        this.canStartAtZero = (minHistoryConsumption > 0);
                    }

                    this.createChart();
                });
            }));
    }

    ngOnDestroy() {
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.createChart();
    }

    private getChartConfig(): any {
        let displaym3YAxis: boolean = false;
        let displaykWhYAxis: boolean = false;
        if (this.m3ConsumptionData.length > 0) {
            displaym3YAxis = true;
        }
        if (this.KwhConsumptionData.length > 0) {
            displaykWhYAxis = true;
        }
        let config = {
            type: 'line',
            options: {

                tooltips: {
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        title: function (tooltipItem) {
                            let callbackDate = new Date(tooltipItem[0].xLabel).toString();
                            return new DatePipe('en-GB').transform(callbackDate, 'dd/MM/yy').toString();
                        },
                    }
                },
                maintainAspectRatio: true,
                steppedLine: true,
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Date',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        type: 'time',
                        displayFormats: {
                            'month': 'MM',
                        },
                        gridLines: {
                            display: false
                        },
                        time: {
                            unit: 'month',
                            unitStepSize: 1,
                            displayFormats: {
                                'month': 'MMM'
                            }
                        }
                    }],
                    yAxes: [
                        {
                            id: "kWh",
                            scaleLabel: {
                                display: displaykWhYAxis,
                                labelString: 'kWh',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            legend: {
                                display: displaykWhYAxis
                            },
                            position: 'left',
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                callback: (label: number, index: number, labels: any) => {
                                    // comma separated thousands regex
                                    return label.toString();
                                },
                                beginAtZero: true
                            }
                        },
                        {
                            id: "m3",
                            display: displaym3YAxis,
                            scaleLabel: {
                                display: displaym3YAxis,
                                labelString: 'm3',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            legend: {
                                display: displaym3YAxis
                            },
                            position: 'right',
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                beginAtZero: this.canStartAtZero,
                                callback: (label: number, index: number, labels: any) => {
                                    // comma separated thousands regex
                                    return label.toString();
                                }
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

            this.heatGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.heatGradientStroke.addColorStop(0, '#ba4423');
            this.heatGradientStroke.addColorStop(1, '#f46036');

            this.heatAGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.heatAGradientStroke.addColorStop(0, '#eb8634');
            this.heatAGradientStroke.addColorStop(1, '#e3761e');

            this.hotWaterGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.hotWaterGradientStroke.addColorStop(0, '#015fb5');
            this.hotWaterGradientStroke.addColorStop(1, '#00539f');

            this.coldWaterGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.coldWaterGradientStroke.addColorStop(0, '#0db5bf');
            this.coldWaterGradientStroke.addColorStop(1, '#009aa3');

            this.electricityGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.electricityGradientStroke.addColorStop(0, '#cebc1a');
            this.electricityGradientStroke.addColorStop(1, '#f9e533');

            this.gasGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.gasGradientStroke.addColorStop(0, '#153954');
            this.gasGradientStroke.addColorStop(1, '#1c5b89');

            this.chillingGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.chillingGradientStroke.addColorStop(0, '#310A31');
            this.chillingGradientStroke.addColorStop(1, '#771977');

            this.VRFGradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
            this.VRFGradientStroke.addColorStop(0, '#ade51d');
            this.VRFGradientStroke.addColorStop(1, '#93c01f');

        let datasets = [] as any;

        if (this.heatConsumptionChartData.length > 0) {
            datasets.push({
                label: 'heat (kWh)',
                yAxisID: "kWh",
                lineTension: 0.2,
                data: this.heatConsumptionChartData,
                spanGaps: true,
                backgroundColor: '#ba4423',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.heatGradientStroke,
                pointBorderColor: this.heatGradientStroke,
                pointBackgroundColor: this.heatGradientStroke,
                pointHoverBackgroundColor: '#ba4423',

            });
        }

        if (this.heatAConsumptionChartData.length > 0) {
            datasets.push({
                label: 'heat A (kWh)',
                yAxisID: "kWh",
                lineTension: 0.2,
                data: this.heatAConsumptionChartData,
                spanGaps: true,
                backgroundColor: '#ba4423',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.heatAGradientStroke,
                pointBorderColor: this.heatAGradientStroke,
                pointBackgroundColor: this.heatAGradientStroke,
                pointHoverBackgroundColor: '#ba4423',

            });
        }

        if (this.hotWaterConsumptionChartData.length > 0) {
            datasets.push({
                label: 'hot water (m³)',
                yAxisID: "m3",
                lineTension: 0.2,
                data: this.hotWaterConsumptionChartData,
                spanGaps: true,
                backgroundColor: '#00539f',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.hotWaterGradientStroke,
                pointBorderColor: this.hotWaterGradientStroke,
                pointBackgroundColor: this.hotWaterGradientStroke,
                pointHoverBackgroundColor: '#00539f',

            });
        }

        if (this.coldWaterConsumptionChartData.length > 0) {
            datasets.push({
                label: 'cold water (m³)',
                yAxisID: "m3",
                lineTension: 0.2,
                data: this.coldWaterConsumptionChartData,
                spanGaps: true,
                backgroundColor: '#009aa3',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.coldWaterGradientStroke,
                pointBorderColor: this.coldWaterGradientStroke,
                pointBackgroundColor: this.coldWaterGradientStroke,
                pointHoverBackgroundColor: '#009aa3',
            });
        }

        if (this.electricityConsumptionData.length > 0) {
            datasets.push({
                label: 'Eletricity (kWh)',
                yAxisID: "kWh",
                lineTension: 0.2,
                data: this.electricityConsumptionData,
                spanGaps: true,
                backgroundColor: '#f9e533',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.electricityGradientStroke,
                pointBorderColor: this.electricityGradientStroke,
                pointBackgroundColor: this.electricityGradientStroke,
                pointHoverBackgroundColor: '#f9e533',
            });
        }

        if (this.gasConsumptionData.length > 0) {
            datasets.push({
                label: 'Gas (kWh)',
                yAxisID: "kWh",
                lineTension: 0.2,
                data: this.gasConsumptionData,
                spanGaps: true,
                backgroundColor: '#1c5b89',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.gasGradientStroke,
                pointBorderColor: this.gasGradientStroke,
                pointBackgroundColor: this.gasGradientStroke,
                pointHoverBackgroundColor: '#1c5b89',
            });
        }

        if (this.VRFConsumptionData.length > 0) {
            datasets.push({
                label: 'VRF (kWh)',
                yAxisID: "kWh",
                lineTension: 0.2,
                data: this.VRFConsumptionData,
                spanGaps: true,
                backgroundColor: '#ade51d',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.VRFGradientStroke,
                pointBorderColor: this.VRFGradientStroke,
                pointBackgroundColor: this.VRFGradientStroke,
                pointHoverBackgroundColor: '#ade51d',
            });
        }

        if (this.chillingConsumtionData.length > 0) {
            datasets.push({
                label: 'Chilling (kWh)',
                yAxisID: "kWh",
                lineTension: 0.2,
                data: this.chillingConsumtionData,
                spanGaps: true,
                backgroundColor: '#310A31',
                fill: false,
                pointRadius: 3,
                borderWidth: 2,
                pointHoverRadius: 5,
                pointHitRadius: 10,
                borderColor: this.chillingGradientStroke,
                pointBorderColor: this.chillingGradientStroke,
                pointBackgroundColor: this.chillingGradientStroke,
                pointHoverBackgroundColor: '#310A31',
            });
        }

        let config = this.getChartConfig();
        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: { datasets: datasets }
        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
